import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, firstValueFrom } from 'rxjs';
import { getAuth } from 'firebase/auth';
import { PriceCodeToTierNameDev, PriceCodeToTierNameProd } from '../constants/subscription-tiers';
import { environment } from 'src/environments/environment';

// B-006: Subscription reads now go through the `get_stripe_subscriptions`
// Cloud Function. The Stripe key no longer ships in the client bundle, and
// the server identifies the user from the Firebase ID token — the customer
// lookup (customers/{uid}.stripeId) happens server-side. The response body
// is the raw Stripe `GET /v1/subscriptions` JSON, so the parsing below is
// unchanged from the previous direct-API implementation.

@Injectable({
  providedIn: 'root'
})
export class StripeService {

  constructor(private http: HttpClient) {}

  private async getAuthHeaders(): Promise<{ Authorization: string } | null> {
    const user = getAuth().currentUser;
    if (!user) {
      return null;
    }
    const token = await user.getIdToken();
    return { Authorization: `Bearer ${token}` };
  }

  async getUserActiveSubscriptions(userId: string): Promise<Observable<any>> {
    try {
      const headers = await this.getAuthHeaders();
      if (!headers) {
        return of(null);
      }
      return this.http.get<any>(`${environment.apiURL}/get_stripe_subscriptions`, { headers });
    } catch (error) {
      console.error('Error fetching user subscriptions:', error);
      throw error; // Propagate error up
    }
  }

  async getUserSubscriptionTier(userId: string) {
    try {
      const headers = await this.getAuthHeaders();
      if (!headers) {
        return null;
      }
      const stripeResponse = await firstValueFrom(this.http.get<any>(
        `${environment.apiURL}/get_stripe_subscriptions`,
        { headers }
      ));

      if (stripeResponse && stripeResponse.data && stripeResponse.data.length > 0) {
        const currentActivePriceCode = stripeResponse.data[0].plan.id;

        let tierName = null;
        if (environment.production) {
          tierName = PriceCodeToTierNameProd[currentActivePriceCode];
        } else {
          tierName = PriceCodeToTierNameDev[currentActivePriceCode];
        }

        if (tierName !== null) {
          return tierName;
        } else {
          return null;
        }
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error fetching user subscription tier:', error);
      throw error;
    }
  }

}
